import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { fadeUp } from './motionPresets';

const dashboardStats = [
  { label: 'Total Employees', val: '247' },
  { label: 'Active Visas', val: '189' },
  { label: 'Expiring Soon', val: '12' },
  { label: 'Compliance Rate', val: '94.2%' },
];

export default function HeroSection() {
  return (
    <section className="relative bg-white pt-20 lg:pt-24 pb-20 lg:pb-24" data-testid="hero-section">
      {/* Gradient card container */}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6">
        <div className="relative overflow-hidden rounded-3xl">
          {/* Gradient background image */}
          <div className="absolute inset-0">
            <img src="/hero-gradient.png" alt="" className="w-full h-full object-cover" />
          </div>

          <div className="relative px-6 lg:px-8 pt-24 pb-48 lg:pt-32 lg:pb-56">
            <div className="max-w-4xl mx-auto text-center">
              <motion.h1
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-normal text-white tracking-tight leading-[1.12] mb-6"
              >
                Simplify <span className="font-bold">H-1B, H-1B1 & E-3</span><br />Public Access File{' '}
                <img src="/document-search.png" alt="" className="inline-block w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 align-middle mx-1" />
                {' '}Compliance
              </motion.h1>

              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed"
              >
                Manage, track, and automate PAF compliance with confidence.<br className="hidden sm:block" />
                <span className="font-semibold text-white">The modern platform trusted by HR teams, immigration attorneys,<br className="hidden sm:block" /> and compliance officers.</span>
              </motion.p>

              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-white font-semibold rounded-full transition-all text-[15px] border border-white/30 hover:bg-white/10 backdrop-blur-sm"
                  data-testid="hero-demo-btn"
                >
                  View Demo
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#0F172A] font-semibold rounded-full transition-all shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/15 active:scale-[0.98] text-[15px]"
                  data-testid="hero-start-btn"
                >
                  Get Started Free <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard mockup — overlapping the gradient card */}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 -mt-36 lg:-mt-44 relative z-10">
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-5xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-2xl shadow-black/30 p-2 lg:p-3">
            <div className="bg-[#F8FAFF] rounded-xl p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-amber-400" />
                <div className="w-3 h-3 rounded-full bg-emerald-400" />
                <span className="ml-3 text-xs text-[#94A3B8] font-medium">PAFHub Dashboard</span>
              </div>
              <div className="grid grid-cols-4 gap-4 mb-6">
                {dashboardStats.map((c) => (
                  <div key={c.label} className="bg-white rounded-xl p-4 border border-indigo-50">
                    <p className="text-2xl font-bold text-[#0F172A]">{c.val}</p>
                    <p className="text-xs text-[#64748B] mt-1">{c.label}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 bg-white rounded-xl p-4 border border-indigo-50 h-32 flex items-center justify-center">
                  <div className="flex items-center gap-6">
                    <div className="relative w-20 h-20">
                      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                        <circle cx="40" cy="40" r="34" fill="none" stroke="#EEF2FF" strokeWidth="6" />
                        <circle cx="40" cy="40" r="34" fill="none" stroke="#4F46E5" strokeWidth="6" strokeDasharray="201 213" strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-[#0F172A]">94%</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-xs text-[#64748B]">Compliant: 178</span></div>
                      <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="text-xs text-[#64748B]">Warning: 8</span></div>
                      <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /><span className="text-xs text-[#64748B]">Non-Compliant: 3</span></div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-indigo-50 space-y-2">
                  <p className="text-xs font-semibold text-[#0F172A] mb-2">Recent Alerts</p>
                  <div className="bg-amber-50 rounded-lg p-2 text-xs text-amber-700">LCA expiring in 15 days</div>
                  <div className="bg-red-50 rounded-lg p-2 text-xs text-red-600">Missing wage docs</div>
                  <div className="bg-emerald-50 rounded-lg p-2 text-xs text-emerald-600">PAF complete</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
